import React, { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { humanizeString } from "@utils/stringMinipulation"
import { api, ParaplannerAttrs } from "../api"
import { toastSuccess, toastError } from "../toast"
import { Button } from "@components/Button"
import { InviteModal, InviteFormValues } from "../components/InviteModal"
import { SearchBox } from "@UILibrary/SearchBox"
import { ConfirmDialog } from "@UILibrary/Modal"
import { Table, TableColumn, SortState, RowActionsMenu, ActionItem, sortRows } from "@UILibrary/Table"
import { ParaplannerShow } from "./ParaplannerShow"

type ParaplannerRow = {
  id: string
  attributes: {
    fullName: string
    email: string
    cellphoneNumber: string
    status: string
    active: boolean
  }
}

// A row action plus the name it reports on.
type Target = { id: number; name: string }
// Invite wording differs for a first send vs a resend, so the toast needs to
// know which one it is reporting.
type InviteTarget = Target & { resend: boolean }

const PER_PAGE = 25

export const ParaplannersTab: React.FC = () => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const { data, isLoading, error } = useQuery(
    ["fsp-admin-paraplanners", search],
    () => api.paraplanners.index(search),
    { keepPreviousData: true }
  )
  const [inviteOpen, setInviteOpen] = useState(false)
  const [selected, setSelected] = useState<{ id: number; fullName: string; email: string } | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<Target | null>(null)
  const [sort, setSort] = useState<SortState>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const createMutation = useMutation(
    (attrs: ParaplannerAttrs) => api.paraplanners.create(attrs),
    {
      onSuccess: (_data, attrs) => {
        queryClient.invalidateQueries(["fsp-admin-paraplanners"])
        toastSuccess(`Invite sent to ${attrs.email}.`)
      },
      // The modal keeps its own inline error next to the form it came from, so
      // no failure toast here.
    }
  )
  // Row actions report through toasts — they're fire-and-forget, so the name has
  // to travel with the id to name the person in the message.
  const inviteMutation = useMutation(
    ({ id }: InviteTarget) => api.paraplanners.invite(id),
    {
      onSuccess: (_data, { name, resend }) => {
        queryClient.invalidateQueries(["fsp-admin-paraplanners"])
        toastSuccess(`Invite ${resend ? "resent" : "sent"} to ${name}.`)
      },
      onError: (error, { name, resend }) =>
        toastError(`Couldn't ${resend ? "resend" : "send"} the invite to ${name}`, error),
    }
  )
  const deactivateMutation = useMutation(
    ({ id }: Target) => api.paraplanners.deactivate(id),
    {
      onSuccess: (_data, { name }) => {
        queryClient.invalidateQueries(["fsp-admin-paraplanners"])
        toastSuccess(`${name} deactivated.`)
      },
      onError: (error, { name }) => toastError(`Couldn't deactivate ${name}`, error),
    }
  )
  const reactivateMutation = useMutation(
    ({ id }: Target) => api.paraplanners.reactivate(id),
    {
      onSuccess: (_data, { name }) => {
        queryClient.invalidateQueries(["fsp-admin-paraplanners"])
        toastSuccess(`${name} reactivated.`)
      },
      onError: (error, { name }) => toastError(`Couldn't reactivate ${name}`, error),
    }
  )


  const rows: ParaplannerRow[] = data?.data ?? []

  const statusOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.attributes.status)))
        .sort()
        .map((s) => ({ value: s, label: humanizeString(s) })),
    [rows]
  )

  const displayRows = useMemo(() => {
    const filtered = statusFilter ? rows.filter((r) => r.attributes.status === statusFilter) : rows
    return sort ? sortRows(filtered, (r) => (r.attributes as Record<string, unknown>)[sort.key], sort.dir) : filtered
  }, [rows, statusFilter, sort])

  useEffect(() => setPage(1), [search, statusFilter, sort])

  const columns: TableColumn<ParaplannerRow>[] = [
    {
      key: "fullName",
      label: "Name",
      sortable: true,
      render: (r) => (
        <Button
          textLink
          onClick={() => setSelected({ id: Number(r.id), fullName: r.attributes.fullName, email: r.attributes.email })}
        >
          {r.attributes.fullName}
        </Button>
      ),
    },
    { key: "email", label: "Email", sortable: true, render: (r) => r.attributes.email },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filterable: true,
      filterOptions: statusOptions,
      render: (r) => humanizeString(r.attributes.status),
    },
    { key: "active", label: "Active", sortable: true, render: (r) => (r.attributes.active ? "Yes" : "No") },
    {
      key: "actions",
      label: "",
      render: (r) => {
        const openShow = () =>
          setSelected({ id: Number(r.id), fullName: r.attributes.fullName, email: r.attributes.email })
        // A deactivated paraplanner can't log in, so inviting them is a dead end
        // — reactivating is the only meaningful action until they're back.
        const items: ActionItem[] = r.attributes.active
          ? [
              ...(r.attributes.status !== "onboarding_complete"
                ? [
                    {
                      label: r.attributes.status === "invited" ? "Resend invite" : "Send invite",
                      onClick: () =>
                        inviteMutation.mutate({
                          id: Number(r.id),
                          name: r.attributes.fullName,
                          resend: r.attributes.status === "invited",
                        }),
                    },
                  ]
                : []),
              {
                label: "Deactivate",
                danger: true,
                onClick: () => setDeactivateTarget({ id: Number(r.id), name: r.attributes.fullName }),
              },
            ]
          : [
              {
                label: "Reactivate",
                onClick: () => reactivateMutation.mutate({ id: Number(r.id), name: r.attributes.fullName }),
              },
            ]
        return (
          <div className="fsp-admin__row-actions">
            <button type="button" className="fsp-admin__row-link" onClick={openShow}>
              Manage assignments
            </button>
            <RowActionsMenu items={items} />
          </div>
        )
      },
    },
  ]

  if (selected) {
    return <ParaplannerShow paraplanner={selected} onBack={() => setSelected(null)} />
  }

  return (
    <div>
      <div className="wb-clients__header">
        <div className="wb-clients__heading">
          <h1>Paraplanners</h1>
          <div className="wb-clients__subtitle">
            {displayRows.length} {displayRows.length === 1 ? "paraplanner" : "paraplanners"}
          </div>
        </div>
        <div className="wb-clients__toolbar">
          <SearchBox onSearch={setSearch} placeholder="Search paraplanners" />
          <button type="button" className="wb-clients__add" onClick={() => setInviteOpen(true)}>+ Invite paraplanner</button>
        </div>
      </div>

      {error ? (
        <p>Failed to load paraplanners.</p>
      ) : (
        <Table
          data={displayRows}
          columns={columns}
          sort={sort}
          onSortChange={setSort}
          activeFilters={{ status: statusFilter }}
          onFilterChange={(_key, value) => setStatusFilter(value)}
          itemsPerPage={PER_PAGE}
          page={page}
          onPageChange={setPage}
          loading={isLoading}
          emptyMessage="No paraplanners found."
        />
      )}

      <InviteModal
        title="Invite Paraplanner"
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={async (values: InviteFormValues) => {
          await createMutation.mutateAsync(values)
        }}
        submitting={createMutation.isLoading}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate paraplanner"
        danger
        confirmLabel="Deactivate"
        busy={deactivateMutation.isLoading}
        message={
          deactivateTarget ? (
            <p style={{ margin: 0 }}>
              Deactivate <strong>{deactivateTarget.name}</strong>? They will lose access until reactivated.
            </p>
          ) : null
        }
        onConfirm={() => {
          if (!deactivateTarget) return
          deactivateMutation.mutate(deactivateTarget, { onSuccess: () => setDeactivateTarget(null) })
        }}
        onClose={() => setDeactivateTarget(null)}
      />
    </div>
  )
}
